import {
  MenuBook as BookIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SecureImage } from '@/components/secure-image';
import { useNovel } from '@/context/novel-context';
import { useImageProcessor } from '@/features/shared/hooks/use-image-processor';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';

export function BookCoverCard() {
  const { novel } = useNovel();
  const { backend } = useFirebase();
  const { processImages, isProcessing } = useImageProcessor();
  const { toast } = useToast();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isHovered, setIsHovered] = useState(false);

  if (!novel.id) return null;

  const coverImage = novel.coverImage;
  const coverImageUrl = coverImage?.original.url || novel.coverImageUrl;
  const coverImagePath = coverImage?.original.path || coverImage?.profile.path;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    try {
      // 1. Process and upload image
      const processed = await processImages([file], novel.id, 'covers', novel.ownerId);
      if (processed && processed.length > 0) {
        const primaryImage = processed[0];

        // 2. Delete old storage files if present
        // 2. Delete old storage files if present
        if (backend && coverImage) {
          await backend.assets.deleteProcessedImage(coverImage);
        }

        // 3. Update database
        if (backend) {
          await backend.novels.updateNovel(novel.ownerId, novel.id, {
            coverImageUrl: primaryImage.original.url,
            coverImage: primaryImage,
          });
        }

        toast({
          title: t('dashboard.cover.upload_success'),
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('Failed to upload cover:', error);
      toast({
        title: t('dashboard.cover.upload_failed'),
        variant: 'destructive',
      });
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteCover = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // 1. Delete storage files
      if (backend && coverImage) {
        await backend.assets.deleteProcessedImage(coverImage);
      }

      // 2. Remove fields from Firestore using backend update (passing undefined)
      if (backend) {
        await backend.novels.updateNovel(novel.ownerId, novel.id, {
          coverImageUrl: undefined,
          coverImage: undefined,
        });
      }

      toast({
        title: t('dashboard.cover.remove_success'),
        variant: 'default',
      });
    } catch (error) {
      console.error('Failed to delete cover:', error);
    }
  };

  const triggerUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <input
        type="file"
        hidden
        accept="image/*"
        ref={fileInputRef}
        onChange={handleFileChange}
        disabled={isProcessing}
      />
      <Card
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={triggerUpload}
        sx={{
          width: 160, height: 240, aspectRatio: '2/3', position: 'relative', borderRadius: 3,
          border: coverImageUrl ? '1px solid' : '2px dashed',
          borderColor: coverImageUrl ? 'divider' : 'rgba(161, 130, 74, 0.4)',
          bgcolor: 'rgba(161, 130, 74, 0.03)', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', cursor: isProcessing ? 'default' : 'pointer',
          overflow: 'hidden', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: 'primary.main', boxShadow: '0 8px 24px rgba(161, 130, 74, 0.15)',
            transform: isProcessing ? 'none' : 'translateY(-2px)',
          },
        }}
      >
        {coverImageUrl ? (
          <SecureImage
            src={coverImageUrl}
            path={coverImagePath}
            alt="Book Cover"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Stack spacing={1} alignItems="center" sx={{ px: 2, textAlign: 'center' }}>
            <BookIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
              {t('dashboard.cover.no_image')}
            </Typography>
          </Stack>
        )}

        {isProcessing && (
          <Box
            sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(20, 16, 12, 0.8)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 1, zIndex: 3,
            }}
          >
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
              {t('dashboard.cover.uploading')}
            </Typography>
          </Box>
        )}

        {!isProcessing && isHovered && (
          <Box
            sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(20, 16, 12, 0.75)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 1.5, zIndex: 2, animation: 'fadeIn 0.2s ease-in-out',
              '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
            }}
          >
            <UploadIcon sx={{ color: 'white', fontSize: 24 }} />
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem' }}>
              {t('dashboard.cover.change')}
            </Typography>

            {coverImageUrl && (
              <Tooltip title={t('dashboard.cover.remove')}>
                <IconButton
                  size="small"
                  onClick={handleDeleteCover}
                  sx={{
                    position: 'absolute', top: 8, right: 8,
                    bgcolor: 'rgba(211, 47, 47, 0.15)', color: 'error.light',
                    '&:hover': { bgcolor: 'error.main', color: 'white' },
                    transition: 'all 0.2s',
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Card>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary', fontWeight: 600, letterSpacing: '0.15em', fontSize: '0.65rem', mt: 0.5,
        }}
      >
        {t('dashboard.cover.title')}
      </Typography>
    </Box>
  );
}

export default BookCoverCard;
